/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {useEffect, useMemo, useState} from "react";
import toast from 'react-hot-toast';
import { usePostRequest } from "../../../../api/helper";
import VAR_COMMON_CONSTANTS from "../../../../constants/globalVariables";
import { hasThisAccessLevelToRouteKey } from "../../../../redux/ducks/AccessControl/accessControlHelpers";
import { EUserPermissionAccess } from "../../../../redux/ducks/AccessControl/types";
import { EArgosRouteKeys } from "../../../../Routes/constants";
import { usersDataEndpoint } from "../../../pages/CoverageRequirements/constants";
import {EDatasetExecutionStatus, MERGE_MODES} from "../../../pages/CoverageRequirements/ItemDetails/constants";
import { User } from "../../../pages/CoverageRequirements/Table/Dialogs/constants";
import ArgosFlexbox from "../../Containers/ArgosFlexbox";
import Loader from "../../Loader/Loader";
import {MultiPageWizard} from "../../MultiPageWizard/MultiPageWizard";
import EditMergeSetDetails from "./EditMergeSetDetails";
import EditMergeSetSourceDatasets from "./EditMergeSetSourceDatasets";
import executionStatus from "../../../pages/CoverageRequirements/ItemDetails/Tabs/ExecutionStatusTab/ExecutionStatus";
import {useAppSelector} from "../../../../redux/hooks";
import {selectDatasetsExecutionStatus} from "../../../../redux/ducks/CoverageRequirements/slice";


interface EditMergeSetProps {
  mergeSetId: string
  onClose: () => void
}

const EditMergeSet = (props: EditMergeSetProps) => {
  const steps = ['Configure properties', `Select ${VAR_COMMON_CONSTANTS.VAR_DATA_SET.toLowerCase()}s`]
  const [isFetchLoading, setIsFetchLoading] = useState<boolean>(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState<boolean>(false)
  const [userData, setUserData] = useState<User[]>([])
  const [userIdNameMap, setUserIdNameMap] = useState({})
  const [mergeSetData, setMergeSetData] = useState<any>({})
  const mergeSetDataIsIncomplete = mergeSetData?.name == "" || mergeSetData?.description == "" 
    || mergeSetData?.owner == "" || (mergeSetData?.mergeSettings && Object.values(mergeSetData?.mergeSettings).every(value => value === false))

  const fetchMergeSetDataEndpoint = "/HttpTriggerCoverageRequirements?request_operation=get&request_type=getMergeSetData"
  const updateMergeSetDataEndpoint = "/HttpTriggerCoverageRequirements?request_operation=update&request_type=updateMergeSetData"
  const fetchUsersHandle = usePostRequest(usersDataEndpoint)
  const fetchHandle = usePostRequest(fetchMergeSetDataEndpoint)
  const updateHandle = usePostRequest(updateMergeSetDataEndpoint)
  
  const fetchUsersData = () => {
    fetchUsersHandle().then((data) => {
      const usersData = data.filter(
        (element: any) =>
          hasThisAccessLevelToRouteKey(EArgosRouteKeys.COVERAGE_REQUIREMENTS, element, EUserPermissionAccess.READ_WRITE)
      )
      const userIdNamesObj = usersData.map((elem: User) => {
        return {
          [elem.id]: elem.firstName + " " + elem.lastName
        }
      })
      const userIdNames = Object.assign({}, ...userIdNamesObj)
      setUserData(usersData)
      setUserIdNameMap(userIdNames)
    }).catch(err => {
      console.log(err)
      toast.error("An error occurred while fetching users data")
    })
  }

  const fetchMergeSetData = (mergeSetId: string) => {
    setIsFetchLoading(true)
    const req = {'mergeSetId': mergeSetId}
    fetchHandle(req).then((resp) => {
      setMergeSetData(resp.mergeSet)
    }).catch((e) => {
      toast.error("An error occurred while fetching the merge set data")
      console.log(e)
    }).finally(() => {
      setIsFetchLoading(false)
    })
  }

  const updateMergeSetData = (mergeSetId: string, data: any) => {
    setIsUpdateLoading(true)
    const req = {'mergeSetId': mergeSetId, 'data': data}
    updateHandle(req).then((resp) => {
      toast.success("Changes saved")
    }).catch((e) => {
      toast.error("An error occurred while updating the merge set data")
      console.log(e)
    }).finally(() => {
      setIsUpdateLoading(false)
      props.onClose()
    })
  }

  useEffect(() => {
    fetchUsersData()
    fetchMergeSetData(props.mergeSetId)
  }, [])

  const handleMergeSetDataChange = (data: any) => {
    setMergeSetData(data)
  }
  
  const handleSave = () => {
    updateMergeSetData(props.mergeSetId, mergeSetData)
  }

  const executionStatusMap: any = useAppSelector(selectDatasetsExecutionStatus)

  const isFinishDisabled = useMemo(() => {
    // if any of the datasets are not Completed or not Invalid, disable the finish button
    const selectedDatasets = mergeSetData?.selectedDatasetIds
    if (!selectedDatasets) return true
    for (const datasetId of selectedDatasets) {
      if (executionStatusMap[datasetId] !== EDatasetExecutionStatus.COMPLETED && executionStatusMap[datasetId] !== EDatasetExecutionStatus.INVALID_RESULTS) {
        return true
      }
    }
    return false
  }, [mergeSetData, executionStatusMap])

  return (
    <ArgosFlexbox vertical style={{justifyContent: "center", alignItems: "center"}}>
      <MultiPageWizard steps={steps} onFinish={() => handleSave()} onClickClose={() => props.onClose()}
                       isNextDisabled={[mergeSetDataIsIncomplete, mergeSetData?.selectedDatasetIds?.length < 2]}
                       isFinishLoading={isUpdateLoading}
                        isFinishDisabled={isFinishDisabled}
      >
        <Loader isLoading={isFetchLoading}>
          <EditMergeSetDetails action={MERGE_MODES.UPDATE} mergeSetData={mergeSetData} userData={userData} onChange={handleMergeSetDataChange}/>
        </Loader>
        <EditMergeSetSourceDatasets mergeSetData={mergeSetData} onChange={handleMergeSetDataChange}/>
      </MultiPageWizard>
    </ArgosFlexbox>
  )
}

export default EditMergeSet
